/**
 * BadgeClaimFlow.jsx
 * Opción B refinada: si está autenticado, reclama la insignia.
 * Si no, muestra aviso inline + CTA a /login.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import { Award, LogIn, Loader } from 'lucide-react';

export default function BadgeClaimFlow({ program, answers, isAuthenticated, onClaimed, onClearDraft }) {
  const navigate = useNavigate();
  const loginReturnUrl = `/p/${program.slug}#ejercicio-final`;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleClaim() {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.post('/badges/claim', {
        programSlug: program.slug,
        answers
      });
      const { badge, alreadyClaimed } = res.data;
      // Guardar en draft para que State 3 sepa el verificationId
      onClaimed(badge);
      if (alreadyClaimed) {
        // La insignia ya existía (otra PC u otro navegador).
        // Conservamos el draft local (las nuevas respuestas quedan en localStorage)
        // y redirigimos al certificado ya emitido — sin mostrar "nueva emisión".
        navigate(`/home/certificates/${badge.verificationId}`, { replace: false });
      } else {
        // Nueva emisión: limpiar draft y redirigir al certificado recién creado.
        if (onClearDraft) onClearDraft();
        navigate(`/home/certificates/${badge.verificationId}`, { replace: false });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al reclamar la insignia. Intenta de nuevo.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  /* ── Sin sesión ── */
  if (!isAuthenticated) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800 leading-relaxed mb-3">
          Tu ejercicio ya está listo. Inicia sesión para guardar tu insignia digital y obtener tu enlace compartible.
        </p>
        <button
          onClick={() => navigate(`/login?return=${encodeURIComponent(loginReturnUrl)}`)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg text-sm transition-colors"
        >
          <LogIn className="w-4 h-4" />
          Iniciar sesión y reclamar insignia
        </button>
        <p className="text-xs text-amber-600 mt-2">
          Tus respuestas se conservan en este navegador. Al volver, el botón de reclamar estará listo.
        </p>
      </div>
    );
  }

  /* ── Con sesión ── */
  return (
    <div className="space-y-3">
      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
      )}
      <button
        onClick={handleClaim}
        disabled={loading}
        className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all shadow-sm ${
          loading
            ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
            : 'bg-[#6F3FF5] hover:bg-purple-700 text-white hover:shadow-md'
        }`}
      >
        {loading ? (
          <><Loader className="w-4 h-4 animate-spin" /> Guardando insignia...</>
        ) : (
          <><Award className="w-4 h-4" /> Reclamar mi insignia</>
        )}
      </button>
    </div>
  );
}
